import type { CaseDefinition, CaseStatus, GameStateSnapshot } from './types';

export interface GalleryUnlockState {
  unlockedCaseIds: string[];
  maxReachedOrder: number;
  allUnlocked: boolean;
  finalCaseCompleted: boolean;
  hasSaveData: boolean;
}

const COMPLETED_CASE_STATUSES: ReadonlySet<CaseStatus> = new Set([
  'truth_suspected',
  'unresolved',
  'misdirected'
]);

export function createGalleryUnlockState(
  snapshots: readonly GameStateSnapshot[],
  cases: readonly CaseDefinition[],
  finalCaseId?: string
): GalleryUnlockState {
  const sortedCases = [...cases].sort((left, right) => left.displayOrder - right.displayOrder);
  const resolvedFinalCaseId = finalCaseId ?? sortedCases.at(-1)?.id ?? 'case8';
  const caseById = new Map(
    sortedCases.map((caseDefinition) => [caseDefinition.id, caseDefinition])
  );
  const explicitlyUnlockedCaseIds = new Set<string>();
  let maxReachedOrder = sortedCases[0]?.displayOrder ?? -1;
  let finalCaseCompleted = false;

  for (const snapshot of snapshots) {
    const progress = snapshot.progress;
    maxReachedOrder = Math.max(maxReachedOrder, getCaseOrder(progress.currentCaseId, caseById));

    for (const caseId of progress.unlockedCaseIds) {
      const order = getCaseOrder(caseId, caseById);
      if (order >= 0) {
        maxReachedOrder = Math.max(maxReachedOrder, order);
        explicitlyUnlockedCaseIds.add(caseId);
      }
    }

    for (const [caseId, status] of Object.entries(progress.caseStatuses)) {
      if (status === 'hidden') {
        continue;
      }

      const order = getCaseOrder(caseId, caseById);
      if (order >= 0) {
        maxReachedOrder = Math.max(maxReachedOrder, order);
        explicitlyUnlockedCaseIds.add(caseId);
      }

      if (caseId === resolvedFinalCaseId && COMPLETED_CASE_STATUSES.has(status)) {
        finalCaseCompleted = true;
      }
    }
  }

  const unlockedCaseIds = sortedCases
    .filter(
      (caseDefinition) =>
        caseDefinition.displayOrder <= maxReachedOrder ||
        explicitlyUnlockedCaseIds.has(caseDefinition.id)
    )
    .map((caseDefinition) => caseDefinition.id);

  return {
    unlockedCaseIds,
    maxReachedOrder,
    allUnlocked: finalCaseCompleted,
    finalCaseCompleted,
    hasSaveData: snapshots.length > 0
  };
}

export function isGalleryItemUnlocked(
  caseIds: readonly string[],
  unlockState: GalleryUnlockState
): boolean {
  if (unlockState.allUnlocked) {
    return true;
  }

  if (caseIds.length === 0) {
    return false;
  }

  const unlockedCaseIds = new Set(unlockState.unlockedCaseIds);
  return caseIds.some((caseId) => unlockedCaseIds.has(caseId));
}

function getCaseOrder(caseId: string, caseById: ReadonlyMap<string, CaseDefinition>): number {
  return caseById.get(caseId)?.displayOrder ?? -1;
}
